import React, { useState, useContext, useEffect, useRef } from 'react'
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';

import { HospitalContext } from '../../contexts/HospitalContext';
import { UserContext } from '../../contexts/UserContext';
import OperatorActionErrorModal from './OperatorActionErrorModal';
import RemoveUserDialog from './RemoveUserDialog';
import PasswordInput from '../common/PasswordInput';
import PasswordRequirements from '../common/PasswordRequirements';
import { validatePassword } from '../../utils/validatePassword';

import './CMSForm.css'
import './ManageUsersForm.css'

window.Buffer = window.Buffer || require("buffer").Buffer;

const ManageHospitalUsersForm = ({ hospital_id, closeForm }) => {
    const { getHospitalUsers, hospitalUsers, setHospitalUsers, createHospitalUser, editHospitalUser, resetHospitalUserPassword, deleteHospitalUser, getHospitals, getHospitalCount, page, setPage, lastSearchRef} = useContext(HospitalContext)
    const { editUser, deleteUser } = useContext(UserContext)
    const [selected, setSelected] = useState(null)
    const [error, setError] = useState('')
    const [errorModal, setErrorModal] = useState(null)
    const [deleteDialogUser, setDeleteDialogUser] = useState(null)
    const [resetState, setResetState] = useState(false)
    const [inputData, setInputData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        position: '',
        password: '',
    })

    const [resetInput, setResetInput] = useState({
        password: '',
        confirm: '',
    })
    const hospitalIdRef = useRef(null)
    const selectedRef = useRef(null)
    selectedRef.current = selected

    useEffect(() => {
        let cancelled = false
        hospitalIdRef.current = hospital_id
        setSelected(null)
        setHospitalUsers([])
        setError('')
        setErrorModal(null)
        if (hospital_id) {
            getHospitalUsers(hospital_id).then((data) => {
                if (!cancelled && data) setHospitalUsers(data)
            })
        }
        return () => {
            cancelled = true
            hospitalIdRef.current = null
            setSelected(null)
            setHospitalUsers([])
            setError('')
            setErrorModal(null)
        }
    }, [hospital_id])

    useEffect(() => {
        if (selected) {
            setInputData({
                first_name: selected.first_name,
                last_name: selected.last_name,
                email: selected.email,
                position: selected.position || '',
            })
        }
        else {
            setInputData({
                first_name: '',
                last_name: '',
                email: '',
                position: '',
                password: '',
            })
        }
        setError('')
        setResetState(false)
        setResetInput({ password: '', confirm: '' })
    }, [selected])

    function handleSelect(data) {
        selectedRef.current = data
        setSelected(data)
        setError('')
        setErrorModal(null)
        setResetState(false)
        setResetInput({ password: '', confirm: '' })
    }

    async function handleClick() {
        if (!emailCheck(inputData.email)) {
            setError('Invalid Email')
            return
        }
        if (!inputData.position?.trim()) {
            setError('Position is required.')
            return
        }
        if (!selected && !validatePassword(inputData.password)) {
            setError('Password does not meet the requirements.')
            return
        }
        setError('')

        const userData = {
            ...inputData,
        }

        try {
            const users = selected
                ? await editHospitalUser(
                    selected._id,
                    { ...userData, organization_id: hospital_id },
                    hospital_id
                )
                : await createHospitalUser({
                    ...userData,
                    organization_id: hospital_id
                })
            if (hospitalIdRef.current === hospital_id && users) {
                setHospitalUsers(users)
                if (!selected && !selectedRef.current) {
                    setInputData({ first_name: '', last_name: '', email: '', position: '', password: '' })
                }
            }
        } catch (err) {
            if (hospitalIdRef.current !== hospital_id) return
            if (selectedRef.current?._id !== selected?._id) return
            const errorData = err.response?.data
            setError(errorData?.errors?.length ? errorData.errors.join(' ') : errorData?.msg || errorData?.message || 'Failed to save user.')
        }
    }

    function handleChange(e) {
        const { name, value } = e.target;

        setInputData({
            ...inputData,
            [name]: value,
        });

    }

    function handleChangePassword(e) {
        const { name, value } = e.target;

        setResetInput({
            ...resetInput,
            [name]: value,
        });
    }

    function emailCheck(email) {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    async function handleReset() {
        if (!validatePassword(resetInput.password)) {
            setError('Password does not meet the requirements.')
            return
        }
        if (resetInput.password !== resetInput.confirm) {
            setError("Passwords do not match")
            return
        }

        try {
            const users = await resetHospitalUserPassword(
                resetInput.password,
                selected._id,
                hospital_id
            )
            if (hospitalIdRef.current !== hospital_id) return
            if (users) setHospitalUsers(users)
        } catch (err) {
            if (hospitalIdRef.current !== hospital_id) return
            if (selectedRef.current?._id !== selected._id) return
            const serverErrors = err.response?.data?.errors;
            setError(serverErrors?.length ? serverErrors.join(' ') : 'Failed to reset password.')
            return
        }

        if (selectedRef.current?._id !== selected._id) return
        setResetInput({
            password: '',
            confirm: '',
        })
        setError('')
        setResetState(false)
    }

    // Opens the confirm dialog; the dialog's buttons decide which flags handleDelete gets.
    function requestDelete() {
        if (!selected) return
        setError('')
        setDeleteDialogUser(selected)
    }

    // `detach` is local (a PUT that drops one membership); the rest are server flags.
    async function handleDelete({ detach = false, ...additionalFlags } = {}) {
        if (!selected) return
        const isDetach = detach
        setDeleteDialogUser(null)
        // The hospital itself is gone on the move_and_delete branches: refresh the list
        // behind this form, step back a page if that emptied the current one, and close.
        const afterOrganizationDeleted = async () => {
            const list = await getHospitals(page, lastSearchRef.current)
            if (list && list.length === 0 && page > 1) {
                setPage(page - 1)
                getHospitals(page - 1, lastSearchRef.current).catch(() => {})
            }
            await getHospitalCount(lastSearchRef.current)
            if (hospitalIdRef.current !== hospital_id) return
            closeForm()
        }

        try {
            // isDetach is checked first: a user who belongs to other organizations must
            // only lose this membership, even when the organization itself is being
            // deleted. Falling into the deleteUser branch would delete their account.
            if (isDetach) {
                // Flags ride at the top level, not inside the user payload: a detach that
                // empties the org is gated server-side exactly like a delete.
                await editUser(selected._id, {
                    first_name: selected.first_name,
                    last_name: selected.last_name,
                    email: selected.email,
                    remove_organization_ids: [hospital_id],
                }, undefined, additionalFlags)
                if (additionalFlags.move_and_delete_organization) {
                    await afterOrganizationDeleted()
                    return
                }
                const users = await getHospitalUsers(hospital_id)
                if (hospitalIdRef.current !== hospital_id) return
                if (users) setHospitalUsers(users)
            } else if (additionalFlags.move_and_delete_organization) {
                await deleteUser(selected._id, undefined, {
                    move_and_delete_organization: true,
                    data: { organization_id: String(hospital_id) },
                })
                await afterOrganizationDeleted()
                return
            } else {
                // keep_organization (when present) spares an org losing its last user.
                const users = await deleteHospitalUser(selected._id, hospital_id, additionalFlags)
                if (hospitalIdRef.current !== hospital_id) return
                if (users) setHospitalUsers(users)
            }
            if (selectedRef.current?._id === selected._id) {
                setSelected(null)
                setErrorModal(null)
            }
        } catch (err) {
            if (hospitalIdRef.current !== hospital_id) return
            if (selectedRef.current?._id !== selected._id) return
            const errorData = err.response?.data;
            if (errorData?.error === 'organization_has_active_subscriptions' ||
                errorData?.error === 'operator_has_active_subscriptions' ||
                errorData?.error === 'require_move_and_delete_organization' ||
                errorData?.error === 'organization_has_assigned_users' ||
                errorData?.error === 'operator_has_assigned_users') {
                setErrorModal(errorData)
                return
            }
            const serverErrors = errorData?.errors;
            setError(serverErrors?.length ? serverErrors.join(' ') : (isDetach ? 'Failed to remove user from the organization.' : 'Failed to delete user.'))
        }
    }

    const handleMoveAndDeleteOperator = () => { if (selected) handleDelete({ move_and_delete_organization: true }) }
    const handleRetry = handleMoveAndDeleteOperator
    const handleCancelErrorModal = () => setErrorModal(null)

    const deleteDialogIsDetach = (deleteDialogUser?.organizations || []).length > 1
    // Last member of this org: the server would otherwise force the org to be deleted.
    const deleteDialogIsLastUser = hospitalUsers.length <= 1

    return (
        <>
        <OperatorActionErrorModal
            error={errorModal}
            onRetry={handleRetry}
            onCancel={handleCancelErrorModal}
            onRetryWithCancelSubscription={handleCancelErrorModal}
            onMoveAndDeleteOperator={handleMoveAndDeleteOperator}
            retryWithCancelLabel="Dismiss"
        />
        <RemoveUserDialog
            user={deleteDialogUser}
            isDetach={deleteDialogIsDetach}
            isLastUser={deleteDialogIsLastUser}
            onCancel={() => setDeleteDialogUser(null)}
            onDetach={() => handleDelete({ detach: true })}
            onDetachKeepOrganization={() => handleDelete({ detach: true, keep_organization: true })}
            onDetachWithOrganization={() => handleDelete({ detach: true, move_and_delete_organization: true })}
            onDelete={() => handleDelete()}
            onDeleteKeepOrganization={() => handleDelete({ keep_organization: true })}
            onDeleteWithOrganization={() => handleDelete({ move_and_delete_organization: true })}
        />
        <div className="operator-user-form" style={{ width: '100%', maxWidth: 'none', padding: 0, maxHeight: 'none' }}>
                <div className="operator-user-select-column">
                    {hospitalUsers.map(e => {
                        return (
                            <p key={String(e._id)} onClick={() => { handleSelect(e) }} className="operator-user-name">
                                {e.first_name} {e.last_name}
                            </p>
                        )
                    })}
                    <p onClick={() => { handleSelect(null) }} style={{ color: '#1976d2' }} className="operator-user-name"> New </p>
                </div>
                <div className="operator-user-input">
                    <div style={{ width: '100%', padding: 0, maxWidth: 'none', maxHeight: 'none', paddingBottom: '0px' }} className="manage-facility-form">
                        {!resetState ?
                            <>
                                {selected ?
                                    <h3 className="manage-facility-form-title">Edit User: {selected.first_name}</h3>
                                    :
                                    <h3 className="manage-facility-form-title">Create New User</h3>
                                }
                                <label htmlFor="first_name">First Name</label>
                                <InputBase className='manger-facility-input' value={inputData.first_name} onChange={(e) => handleChange(e)} type="text" name="first_name" id="first_name" placeholder='User First Name' />

                                <label htmlFor="last_name">Last Name</label>
                                <InputBase className='manger-facility-input' value={inputData.last_name} onChange={(e) => handleChange(e)} type="text" name="last_name" id="last_name" placeholder='User Last Name' />

                                <label htmlFor="email">Email</label>
                                <InputBase className='manger-facility-input' value={inputData.email} onChange={(e) => handleChange(e)} type="text" name="email" id="email" placeholder='Email' />

                                <label htmlFor="position">Position</label>
                                <InputBase className='manger-facility-input' value={inputData.position} onChange={(e) => handleChange(e)} type="text" name="position" id="position" placeholder='e.g. Case Manager' />

                                {!selected &&
                                    <>
                                        <label htmlFor="password">Password</label>
                                        <PasswordInput autoComplete="new-password" className='manger-facility-input' value={inputData.password || ''} onChange={(e) => handleChange(e)} name="password" id="password" placeholder='Password' />
                                        <PasswordRequirements password={inputData.password} />
                                    </>
                                }

                                <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                                    <Button className='manger-facility-btn' onClick={() => handleClick()}>Save</Button>
                                </div>
                                {selected &&
                                    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '10px' }}>
                                        <Button className='manger-facility-btn' onClick={() => { setResetState(true); setError(''); }}>Reset Password</Button>
                                    </div>
                                }
                                {selected &&
                                    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '10px' }}>
                                        <Button style={{ backgroundColor: '#E23844' }} className='manger-facility-btn' onClick={() => { requestDelete() }}>{(selected.organizations || []).length > 1 ? 'Remove' : 'Delete'}</Button>
                                    </div>
                                }
                                <p style={{ textAlign: 'center', margin: '5px 0px' }}>{error}</p>
                            </>
                            :
                            <>
                                <h3 className="manage-facility-form-title">Reset Password For: {selected.first_name}</h3>

                                <label htmlFor="password">Password</label>
                                <PasswordInput autoComplete="new-password" className='manger-facility-input' value={resetInput.password} onChange={(e) => handleChangePassword(e)} name="password" id="password" placeholder='New Password' />
                                <PasswordRequirements password={resetInput.password} />

                                <label htmlFor="confirm">Confirm Password</label>
                                <PasswordInput autoComplete="new-password" className='manger-facility-input' value={resetInput.confirm} onChange={(e) => handleChangePassword(e)} name="confirm" id="confirm" placeholder='Confirm Password' />

                                <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '10px' }}>
                                    <Button className='manger-facility-btn' onClick={() => handleReset()}>reset</Button>
                                </div>
                                <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '10px' }}>
                                    <Button className='manger-facility-btn' onClick={() => { setResetState(false); setResetInput({ password: '', confirm: '' }); setError(''); }}>Cancel</Button>
                                </div>
                                <p style={{ textAlign: 'center', margin: '5px 0px' }}>{error}</p>
                            </>
                        }
                    </div>
                </div>
            </div>
        </>
    )
}

export default ManageHospitalUsersForm
